import { CHAT_PAGE_TITLE, PRODUCT_TITLE, isFleetConsoleTab, type Tab } from "../navigation.ts";
/**
 * JAISIU-4498 — the browser tab title must name the chat the tab is
 * showing, so an operator with several chats open in parallel browser
 * tabs can tell them apart from the tab strip.
 *
 * Pure helpers, no DOM side effects beyond the thin `applyDocumentTitle`
 * wrapper at the bottom — the title logic is unit-tested directly.
 */
import type { SessionsListResult } from "../types.ts";
import { sessionTitle } from "./chat-history-catalog.ts";

/**
 * Re-exported so callers that reach for the browser-title constants keep a
 * single import site. "Fleet Control" is no longer a page name (it is
 * reserved for the fleet-visibility page), so the product title is "Jaisiu".
 */
export { CHAT_PAGE_TITLE, PRODUCT_TITLE };

/**
 * How long a chat title may be before it is truncated. Browser tab
 * strips ellipsize anyway; keeping the title short means the useful
 * part survives the truncation instead of the middle of a sentence.
 */
export const MAX_DOCUMENT_TITLE_LENGTH = 60;

/**
 * Resolve the display title for a session row, matching exactly what
 * the chat-history sidebar shows for that row.
 *
 * Reuses `sessionTitle()` so the browser tab title and the sidebar row
 * can never disagree — the sidebar is the operator's reference for
 * "what is this chat called".
 */
export function resolveChatDocumentTitle(params: {
  sessionKey: string;
  sessions: SessionsListResult | null;
  tabIndex?: number;
}): string {
  const key = params.sessionKey.trim();
  if (!key) {
    return "";
  }
  const row = params.sessions?.sessions?.find((r) => r.key === key);
  if (!row) {
    // The catalog has not loaded yet (or the session is not in it).
    // `sessionTitle`'s sticky fallback yields "Chat 1" / "Main" etc.
    return sessionTitle({ key } as never, params.tabIndex ?? 0);
  }
  return sessionTitle(row, params.tabIndex ?? 0);
}

/**
 * Truncate a chat title for use in a browser tab title.
 *
 * The ellipsis is applied on the RIGHT so the beginning of the title —
 * the part that identifies the chat — is what survives.
 */
export function truncateDocumentTitle(title: string, max = MAX_DOCUMENT_TITLE_LENGTH): string {
  const trimmed = title.trim();
  if (trimmed.length <= max) {
    return trimmed;
  }
  if (max <= 1) {
    return trimmed.slice(0, Math.max(0, max));
  }
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Compute the document title for the current app state.
 *
 * - On the chat tab with an active session → the chat's display title.
 * - On the chat tab with no session yet → the page name ("Chat").
 * - On any other tab → the product name ("Jaisiu").
 */
export function resolveDocumentTitle(params: {
  tab: Tab;
  sessionKey?: string;
  sessions: SessionsListResult | null;
  tabIndex?: number;
}): string {
  if (!isFleetConsoleTab(params.tab)) {
    return PRODUCT_TITLE;
  }
  const chatTitle = resolveChatDocumentTitle({
    sessionKey: params.sessionKey ?? "",
    sessions: params.sessions,
    tabIndex: params.tabIndex,
  });
  if (!chatTitle) {
    return CHAT_PAGE_TITLE;
  }
  return truncateDocumentTitle(chatTitle);
}

/**
 * Write the resolved title to `document.title`.
 *
 * No-ops when the title is unchanged, so repeated renders do not
 * churn the browser's title bar (and do not re-trigger favicon/title
 * observers unnecessarily).
 */
export function applyDocumentTitle(next: string): void {
  if (typeof document === "undefined") {
    return;
  }
  if (document.title === next) {
    return;
  }
  document.title = next;
}
